import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { uploadFile, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Send, Paperclip, MessageSquare, Loader2, HardHat, FileText, AtSign, Hash, UploadCloud } from "lucide-react";
import { toast } from "sonner";
import { serializeMessage, plainText } from "@/lib/mentions";
import MentionInput from "@/components/chat/MentionInput";
import MessageBubble from "@/components/chat/MessageBubble";
import AttachmentCard from "@/components/chat/AttachmentCard";
import { confirm } from "@/components/ConfirmDialog";

const MAX_MB = 15;

export default function Chat() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const navigate = useNavigate();
  const [channels, setChannels] = useState([]);
  const [active, setActive] = useState(isAdmin ? null : user.id);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [mentions, setMentions] = useState([]);
  const [pending, setPending] = useState([]);     // yüklenmiş ama henüz gönderilmemiş ekler
  const [uploading, setUploading] = useState(0);
  const [editing, setEditing] = useState(null);   // {id, text, mentions}
  const [chatCfg, setChatCfg] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const endRef = useRef(null);

  const loadChannels = useCallback(() => {
    if (isAdmin) api.get("/chat/channels").then((r) => setChannels(r.data)).catch(() => {});
  }, [isAdmin]);
  const loadMessages = useCallback(() => {
    if (!active) return;
    api.get(`/chat/messages${isAdmin ? `?channel=${active}` : ""}`).then((r) => setMessages((p) => (JSON.stringify(p) === JSON.stringify(r.data) ? p : r.data))).catch(() => {});
  }, [active, isAdmin]);

  useEffect(() => { loadChannels(); if (isAdmin) api.get("/chat/settings").then((r) => setChatCfg(r.data)).catch(() => {}); }, [loadChannels, isAdmin]);
  useEffect(() => {
    loadMessages();
    const iv = setInterval(() => { loadMessages(); loadChannels(); }, 4000);
    return () => clearInterval(iv);
  }, [loadMessages, loadChannels]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages.length]);

  const send = async () => {
    if (!active) return;
    const { text: body, mentions: used } = serializeMessage(text.trim(), mentions);
    if (!body && !pending.length) return;
    try {
      await api.post("/chat/messages", { channel: active, text: body, attachments: pending, mentions: used });
      setText(""); setMentions([]); setPending([]); loadMessages(); loadChannels();
    } catch (e) { toast.error(apiError(e)); }
  };
  const addFiles = async (files) => {
    const list = [...(files || [])];
    if (!list.length) return;
    if (!active) { toast.error("Önce bir sohbet seçin"); return; }
    for (const f of list) {
      if (f.size > MAX_MB * 1024 * 1024) { toast.error(`${f.name}: ${MAX_MB} MB sınırı aşıldı`); continue; }
      setUploading((n) => n + 1);
      try {
        const { url } = await uploadFile(f);
        setPending((p) => [...p, { url, name: f.name, type: f.type || "", size: f.size }]);
      } catch (err) { toast.error(`${f.name}: yükleme başarısız · ${apiError(err)}`); }
      finally { setUploading((n) => n - 1); }
    }
  };
  const onPaste = (e) => { const fs = [...(e.clipboardData?.files || [])]; if (fs.length) { e.preventDefault(); addFiles(fs); } };
  const onDrop = (e) => { e.preventDefault(); setDragOver(false); addFiles(e.dataTransfer?.files); };

  const startEdit = (m) => setEditing({ id: m.id, text: plainText(m.text), mentions: m.mentions || [] });
  const saveEdit = async () => {
    if (!editing) return;
    const { text: body } = serializeMessage(editing.text.trim(), editing.mentions);
    if (!body) return toast.error("Mesaj boş olamaz");
    try { await api.put(`/chat/messages/${editing.id}`, { text: body }); setEditing(null); loadMessages(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const delMsg = async (m) => {
    if (!await confirm("Bu mesaj silinsin mi?")) return;
    try { await api.delete(`/chat/messages/${m.id}`); loadMessages(); loadChannels(); toast.success("Mesaj silindi"); }
    catch (e) { toast.error(apiError(e)); }
  };
  const toggleQuoteNotify = async (v) => {
    try { const { data } = await api.put("/chat/settings", { quote_notify: v }); setChatCfg(data); toast.success(v ? "Yeni teklif bildirimleri açık — her teklif personel sohbetine düşer" : "Teklif bildirimleri kapatıldı"); }
    catch (e) { toast.error(apiError(e)); }
  };
  const onMentionClick = (seg) => {
    if (seg.kind === "product") navigate(`/stok?q=${encodeURIComponent(seg.label)}`);
    else if (isAdmin) navigate(`/cari?q=${encodeURIComponent(seg.label)}`);
    else toast.info("Cari kartlarına yalnızca yönetici erişebilir");
  };

  const previewOf = (m) => (!m ? "Mesaj yok" : m.deleted ? "Mesaj silindi" : m.system ? m.text : (plainText(m.text) || (m.attachments?.length ? `📎 ${m.attachments[0].name || "Dosya"}` : "📷 Fotoğraf")));
  const activeWorker = channels.find((c) => c.worker.id === active)?.worker;

  return (
    <div className="space-y-6 stagger-in" data-testid="chat-page">
      <PageHeader title="Sohbet" subtitle={isAdmin ? "Depo personeli ile anlık yazışma · dosya & fotoğraf · @cari #ürün etiketleme" : "Yönetici ile anlık yazışma · dosya & fotoğraf · #ürün etiketleme"}>
        {isAdmin && chatCfg && (
          <label className="flex items-center gap-2 rounded-lg border bg-card px-3 py-2 cursor-pointer" data-testid="chat-quote-notify">
            <Switch checked={!!chatCfg.quote_notify} onCheckedChange={toggleQuoteNotify} data-testid="chat-quote-notify-switch" />
            <span className="text-sm"><FileText className="h-3.5 w-3.5 inline mr-1 -mt-0.5 text-secondary" />Yeni teklif oluşunca personele sohbetten bildir</span>
          </label>
        )}
      </PageHeader>

      <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-4 h-[calc(100vh-260px)] min-h-[520px]">
        {isAdmin && (
          <div className="bg-card border rounded-lg overflow-y-auto" data-testid="chat-channels">
            <div className="p-3 border-b font-head font-bold text-sm uppercase tracking-wider text-muted-foreground">Personel</div>
            {channels.length === 0 && <div className="p-4 text-sm text-muted-foreground">Henüz personel yok.</div>}
            {channels.map((c) => (
              <button key={c.worker.id} onClick={() => setActive(c.worker.id)} data-testid={`chat-channel-${c.worker.id}`}
                className={`w-full flex items-center gap-3 p-3 border-b text-left hover:bg-muted/50 ${active === c.worker.id ? "bg-primary/5 border-l-2 border-l-primary" : ""}`}>
                <div className="h-10 w-10 rounded-full bg-secondary/15 text-secondary flex items-center justify-center font-bold shrink-0">{c.worker.name[0]?.toUpperCase()}</div>
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-sm truncate">{c.worker.name}</div>
                  <div className="text-xs text-muted-foreground truncate">{previewOf(c.last_message)}</div>
                </div>
                {c.unread > 0 && <Badge className="shrink-0">{c.unread}</Badge>}
              </button>
            ))}
          </div>
        )}

        <div className="bg-card border rounded-lg flex flex-col overflow-hidden relative"
          onDragOver={(e) => { e.preventDefault(); if (active) setDragOver(true); }} onDragLeave={() => setDragOver(false)} onDrop={onDrop}>
          {dragOver && (
            <div className="absolute inset-0 z-20 bg-primary/10 border-2 border-dashed border-primary rounded-lg flex flex-col items-center justify-center text-primary pointer-events-none" data-testid="chat-dropzone">
              <UploadCloud className="h-10 w-10 mb-2" /><div className="font-semibold">Dosyaları buraya bırakın</div>
            </div>
          )}
          {!active ? (
            <div className="flex-1 flex flex-col items-center justify-center text-muted-foreground">
              <MessageSquare className="h-12 w-12 opacity-30 mb-3" />
              <p>Bir personel seçin</p>
            </div>
          ) : (
            <>
              <div className="p-3 border-b flex items-center gap-3">
                <div className="h-9 w-9 rounded-full bg-secondary/15 text-secondary flex items-center justify-center font-bold">
                  {isAdmin ? (activeWorker?.name?.[0]?.toUpperCase() || "?") : <HardHat className="h-4 w-4" />}
                </div>
                <div className="font-semibold">{isAdmin ? (activeWorker?.name || "Personel") : "Yönetici"}</div>
                <span className="ml-auto text-[11px] text-muted-foreground hidden sm:inline">✓ iletildi · ✓✓ okundu · mesajın üzerine gelin: düzenle / sil</span>
              </div>

              <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-muted/20" data-testid="chat-messages">
                {messages.map((m) => (
                  <MessageBubble key={m.id} m={m} mine={m.sender_id === user.id} isAdmin={isAdmin} editing={editing} setEditing={setEditing}
                    startEdit={startEdit} saveEdit={saveEdit} delMsg={delMsg} onMentionClick={onMentionClick} />
                ))}
                <div ref={endRef} />
              </div>

              <div className="p-3 border-t space-y-2">
                {pending.length > 0 && (
                  <div className="flex flex-wrap gap-3 px-1 pt-1" data-testid="chat-pending">
                    {pending.map((a, i) => <AttachmentCard key={a.url} a={a} compact onRemove={() => setPending((p) => p.filter((_, j) => j !== i))} testid={`chat-pending-${i}`} />)}
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <label className="cursor-pointer shrink-0" title="Dosya / görsel ekle (çoklu)">
                    <input type="file" multiple className="hidden" onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} data-testid="chat-file-input" />
                    <div className="h-10 w-10 rounded-full hover:bg-muted flex items-center justify-center">
                      {uploading ? <Loader2 className="h-5 w-5 animate-spin text-primary" /> : <Paperclip className="h-5 w-5 text-secondary" />}
                    </div>
                  </label>
                  <MentionInput value={text} onChange={setText} onPick={(mn) => setMentions((p) => [...p.filter((x) => x.id !== mn.id), mn])} onSend={send} onPaste={onPaste}
                    placeholder={isAdmin ? "Mesaj yazın… @cari  #ürün ile etiketle" : "Mesaj yazın… #ürün ile etiketle"} />
                  <Button size="icon" className="h-10 w-10 shrink-0" onClick={send} disabled={!!uploading} data-testid="chat-send"><Send className="h-4 w-4" /></Button>
                </div>
                <div className="text-[10px] text-muted-foreground flex items-center gap-3 px-1 flex-wrap">
                  {isAdmin && <span className="inline-flex items-center gap-0.5"><AtSign className="h-3 w-3" />cari etiketi</span>}
                  <span className="inline-flex items-center gap-0.5"><Hash className="h-3 w-3" />ürün etiketi</span>
                  <span className="inline-flex items-center gap-0.5"><Paperclip className="h-3 w-3" />her tür dosya · sürükle-bırak / yapıştır · en fazla {MAX_MB} MB</span>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
